import cardService from '@/services/card.service'
import CookieService from '@/services/cookie.service'
import { store } from '@/store/store'
import { CardListType } from '@/types/page'
import { CardType } from '@/types/page'
import { RecCardType } from '@/types/page'
import { groupByModes } from '@/utils/constants'
import { Mode } from '@/utils/constants'
import { cardListsToNames } from '@/utils/helpers'

import { sort } from './sortBy'
import { SET_STATE } from './types'

type groupProps = {
  cardLists: CardListType[]
  cardMap: Record<string, CardType>
  mode: (typeof groupByModes)[number]['key']
}

export const group = ({ cardLists, cardMap, mode }: groupProps) => {
  const flattened = cardLists.reduce((acc: RecCardType[], section) => [...acc, ...section.cardviews], [])
  switch (mode) {
    case 'default':
      return cardLists
    case 'date': {
      const reducer = (
        acc: Record<string, { header: string; tag: string; cardviews: RecCardType[] }>,
        card: RecCardType,
      ) => {
        const dateRanges = [
          { label: 'Last 3 Months', date: new Date(new Date().setDate(new Date().getDate() - 90)) },
          { label: 'Last 6 Months', date: new Date(new Date().setDate(new Date().getDate() - 180)) },
          { label: 'Last Year', date: new Date(new Date().setDate(new Date().getDate() - 365)) },
          { label: 'Last 2 Years', date: new Date(new Date().setDate(new Date().getDate() - 730)) },
          { label: 'Since 2020', date: new Date('2020-01-01') },
          { label: 'Since 2010', date: new Date('2010-01-01') },
          { label: 'Since 2000', date: new Date('2000-01-01') },
          { label: 'Earliest', date: new Date('0000-01-01') },
        ]
        const released_at = cardMap[card.sanitized_wo]?.released_at
        if (!released_at) return acc
        const dateRange =
          dateRanges.find(({ date }) => new Date(released_at) > date) || dateRanges[dateRanges.length - 1]
        const i = dateRanges.indexOf(dateRange)
        const { label } = dateRanges[i]
        return {
          ...acc,
          [i]: {
            header: label,
            tag: label,
            cardviews: [...(acc[i]?.cardviews || []), card],
          },
        }
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      )
      return sorted
    }
    case 'mana': {
      const reducer = (acc: Record<string, RecCardType[]>, card: RecCardType) => {
        const cmc = (card.cards ? cardMap[card.cards[0].url] : cardMap[card.sanitized_wo]).cmc
        return {
          ...acc,
          [cmc]: [...(acc[cmc] || []), card],
        }
      }
      const sorted = Object.entries(flattened.reduce(reducer, {})).map(
        ([cmc, grouped]) =>
          ({
            cardviews: grouped,
            header: `${cmc} Mana`,
            tag: `${cmc}`,
          }) as CardListType,
      )
      return sorted
    }
    case 'price': {
      const priceRanges = [
        { label: '< $0.25', min: 0, max: 0.25 },
        { label: '$0.25 - $0.50', min: 0.25, max: 0.5 },
        { label: '$0.50 - $1', min: 0.5, max: 1 },
        { label: '$1 - $2.50', min: 1, max: 2.5 },
        { label: '$2.50 - $5', min: 2.5, max: 5 },
        { label: '$5 - $10', min: 5, max: 10 },
        { label: '$10 - $20', min: 10, max: 20 },
        { label: '$20 - $50', min: 20, max: 50 },
        { label: '$50 - $100', min: 50, max: 100 },
        { label: '$100+', min: 100, max: Infinity },
      ]
      const reducer = (
        acc: Record<string, { header: string; tag: string; cardviews: RecCardType[] }>,
        card: RecCardType,
      ) => {
        const price = (card.cards ? cardMap[card.cards[0].url] : cardMap[card.sanitized_wo]).prices.tcgplayer?.price
        if (!price) {
          return {
            ...acc,
            [priceRanges.length]: {
              header: 'Unknown',
              tag: 'Unknown',
              cardviews: [...(acc[priceRanges.length]?.cardviews || []), card],
            },
          }
        }
        const priceRange = priceRanges.find(({ max }) => price < max) || priceRanges[priceRanges.length - 1]
        const i = priceRanges.indexOf(priceRange)
        const { label } = priceRanges[i]
        return {
          ...acc,
          [i]: {
            header: label,
            tag: label,
            cardviews: [...(acc[i]?.cardviews || []), card],
          },
        }
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        // @ts-ignore
        // Can't figure out why the first return in this switch case causes a type error
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      ) as CardListType[]
      return sorted
    }
    case 'rarity': {
      const rarities = ['common', 'uncommon', 'rare', 'mythic', 'special']
      const reducer = (
        acc: Record<string, { header: string; tag: string; cardviews: RecCardType[] }>,
        card: RecCardType,
      ) => {
        const rarity = cardMap[card.sanitized_wo]?.rarity
        if (!rarity) return acc
        const i = rarities.indexOf(rarity)
        return {
          ...acc,
          [i]: {
            header: rarity[0].toUpperCase() + rarity.slice(1),
            tag: rarity,
            cardviews: [...(acc[i]?.cardviews || []), card],
          },
        }
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      )
      return sorted
    }
    case 'type': {
      const types = ['Creature', 'Instant', 'Sorcery', 'Artifact', 'Enchantment', 'Battle', 'Planeswalker', 'Land']
      const reducer = (
        acc: Record<string, { header: string; tag: string; cardviews: RecCardType[] }>,
        card: RecCardType,
      ) => {
        const type = cardMap[card.sanitized_wo]?.primary_type
        if (!type) return acc
        const i = types.indexOf(type)
        return {
          ...acc,
          [i]: {
            header: type,
            tag: type,
            cardviews: [...(acc[i]?.cardviews || []), card],
          },
        }
      }
      const sorted = Object.values(flattened.reduce(reducer, {})).map(
        ({ cardviews, header, tag }) =>
          ({
            cardviews,
            header,
            tag,
          }) as CardListType,
      )
      return sorted
    }
    case 'ungrouped':
      return [{ cardviews: flattened, header: '', tag: '' }]
    default:
      throw new Error(`Unknown group mode: ${mode}`)
  }
}

export const groupBy = async (mode: string) => {
  const dispatch = store.dispatch
  const { cardLists, initCardLists, sortedBy } = store.getState().cardlists
  const { skipFetch } = groupByModes.find((m) => m.key === mode) as Mode

  dispatch({ type: SET_STATE, payload: { loading: true } })
  CookieService.set('groupedBy', mode)

  // Fetch full card data
  // Always fetches, but only awaits if data is needed now
  let cardMap = {}
  if (!skipFetch) {
    cardMap = await cardService.getCardMap(cardListsToNames(cardLists))
  }

  // Group cards
  const newCardLists = group({ cardLists: mode === 'default' ? initCardLists : cardLists, cardMap, mode })

  // Sort cards if needed
  const newNewCardLists =
    sortedBy && sortedBy !== 'default' ? sort({ cardLists: newCardLists, mode: sortedBy, cardMap }) : newCardLists

  dispatch({
    type: SET_STATE,
    payload: {
      cardLists: newNewCardLists,
      groupedBy: mode,
      loading: false,
      updatedAt: Date.now(),
    },
  })
}
